import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';
import * as Constants from 'src/constants';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-04-20'
        tag='Mercari'
        title='Declutter and Boost Your Sales with Mercari Spring Items'
      >
        <p>
          Online marketplaces are a treasure trove of new or used items that can be listed and sold at any given time
          all year round. However, it is only logical and strategic to list items in season because the demand is
          already there.
        </p>
        <p>
          Buyers are looking for in-season things out of necessity and a sense of urgency. There is a pressing need to
          find the right pair of swimwear for an upcoming beach trip during summer and the pressure to find the ideal
          sweater for a Christmas dinner. In these cases, they would most likely buy right away instead of delaying the
          purchase or, worse, completely forget about it.
        </p>
        <p>Simply put, selling in-season items gives sellers better chances of converting listings into sales.</p>

        <h3>What items are in demand during spring?</h3>
        <p>
          First, keep in mind the kinds of activities people do in spring. The pleasant weather – neither too hot nor
          too cold – makes it perfect for various outdoor activities with family and friends. There are also holidays
          and special celebrations held during this season, which is another reason to get together for fun activities.
        </p>
        <p>With these factors in mind, it would make sense to list these items on Mercari:</p>
        <ul>
          <li>
            <strong>Lightweight clothes.</strong> Think thin sweaters and tank tops that are great for layering, breezy
            dresses for picnics, and casual outfits that are comfortable but presentable enough to wear for such
            activities as strolling at the park or biking around the city.
          </li>
          <li>
            <strong>Accessories.</strong> List items that would complement the kinds of clothes you're selling this
            season. Consider hats, caps, sunglasses, scarfs, and even sports watches.
          </li>
          <li>
            <strong>Athletic shoes, including those for trekking.</strong> Springtime is a season to get moving. There
            are streets to hit, mountains to climb, nature to discover, and people would want to do these activities
            with appropriate footwear on.
          </li>
          <li>
            <strong>Essentials for outdoor activities.</strong> During spring, camping and hiking gear (such as tents
            and backpacks) and things needed for a backyard barbecue or a picnic (such as coolers and wicker baskets)
            are relevant.
          </li>
          <li>
            <strong>Mother's Day gifts.</strong> Celebrated on the second Sunday of May, Mother's Day is a good reason
            to list handbags, jewelry, keepsakes, and novelty items targeted at moms.{' '}
          </li>
          <li>
            <strong>Toys and Easter-themed décor.</strong> Some families choose to spend time at home during spring
            break, so it also makes sense to list items for entertainment, such as toys, board games, and activity kits.
            Video games and accessories are a popular category on Mercari; you might want to list them under that too.
            Also, consider selling accessories and decorative items related to Easter.{' '}
          </li>
        </ul>
        <p>
          The in-season items mentioned above may be hidden at the back of your closet or drowned under a pile of
          general clutter. Here's what you can do to make your decluttering spree efficient and fruitful:
        </p>
        <p>
          First, have a game plan. Determine the areas you'd like to tackle and start with the easiest spots to avoid
          getting overwhelmed. Allot a specific time for each area to remain conscious of how you're spending your time.
          Set the alarm if you can't trust yourself to glance at the clock every so often.
        </p>
        <p>
          Second, have bins and boxes ready for each kind of category your spring items will fall under based on the
          categories mentioned earlier. Sort as you declutter.{' '}
        </p>
        <p>
          Third, keep an eye on small items that may not sell as-is but would be great to include as freebies. This
          could be scrunchies or a headband that would look great with the sundress you are listing.
        </p>

        <h3>Tips to get your Mercari spring items to sell faster</h3>
        <p>
          Knowing what to list for a particular season is one thing; optimizing your listing so that it gets sold is
          another. There is no rocket science here, just a simple template with easy tips to make your listings look
          more polished and irresistible to buyers.
        </p>
        <ul>
          <li>
            <p>
              <strong>Start with a sharp and compact title.</strong> Mercari provides a maximum of 80 characters for the
              title. Use it well. It would be wise to include NWT or NWOT, the brand name, and the style whenever
              applicable. Consider how the title would look like when displayed – ideally, it should not be awkwardly
              cut. If the brand's name is too long (e.g., "Realization Par"), you might want to leave that out and go
              right ahead with the style name. The brand's name, along with the size, will appear on the thumbnail
              anyway.
            </p>
          </li>
          <li>
            <p>
              <strong>Fortify with description in bullet points.</strong> Mercari is a bit generous with the space
              allotted for description: 1,000 characters max. Make important details easier to read by writing them in
              bullet points – three is a good number. Include the size, color, material, and condition of the item if
              you haven't already in the title. Mention the features and selling points and any flaws and imperfections
              to manage buyers' expectations.
            </p>
            <p>If listing clothes, bags, and accessories, it would be great to include styling suggestions.</p>
          </li>
          <li>
            <p>
              <strong>Finish off with three hashtags.</strong> Each listing is allowed three hashtags. For your spring
              listings, you may want to include #SpringFashion or #SpringOOTD.
            </p>
          </li>
        </ul>

        <p>
          Although this article focuses on selling on Mercari, some of you also sell on other reselling platforms like
          Poshmark. These tips work on those sites, too! Instead of manually listing from one marketplace to another and
          getting everything tragically mixed up, consider subscribing to a system that can handle cross-listing way
          more efficiently and intelligently.
        </p>
        <p>
          OneShop is the leading operating system for online sellers. It can do all of your work at once and sell
          everywhere. You wouldn't have to list from scratch every single time. You just do it once, and the system even
          makes it easier for you with smart autofill and description templates. When an item gets sold, it
          automatically gets delisted from other platforms.
        </p>
        <p>
          Sign up for the{' '}
          <a href='https://tools.oneshop.com/signup'>
            free <>{Constants.Marketing.TrialDuration}</>-day trial
          </a>{' '}
          to see what we mean – and what you're missing out on – today!
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
